import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, combineLatest, timer } from 'rxjs';
import {
  takeUntil,
  map,
  tap,
  distinctUntilChanged,
  share,
  filter
} from 'rxjs/operators';

import {
  ConnectionStatus,
  NetworkQuality,
  ConnectionState,
  ConnectionConfig,
  WebSocketMessage,
  ConnectionEvent,
  HeartbeatMessage,
  PongMessage,
  AckMessage,
  UserEventMessage,
  ConversationEventMessage,
  MessageEventMessage,
  StateEventMessage,
  PingMessage,
  ChatMessage,
  StatusMessage
} from '../models';import { WebSocketConnectionService } from './websocket-connection.service';
import { MessageRouterService } from './message-router.service';
import { ReconnectionService } from './reconnection.service';
import { NetworkQualityService } from './network-quality.service';
import { CircuitBreakerService } from './circuit-breaker.service';

/**
 * Main WebSocket manager service - thin orchestration layer
 * Coordinates all WebSocket-related services and provides a unified API
 */
@Injectable({
  providedIn: 'root'
})
export class WebSocketManagerService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private pingTimer: number | null = null;
  private lastConnectedTime: Date | undefined;
  private lastError: string | undefined;

  // Enhanced ping management
  private pingSequenceNumber: number = 0;
  private missedPingResponses: number = 0;
  private lastPingTime: number = 0;
  private pingHealthTimer: number | null = null;

  private config: ConnectionConfig = {
    maxReconnectAttempts: 10,
    baseReconnectDelay: 1000,
    maxReconnectDelay: 20000,    // Reduced from 30000ms for faster recovery
    backoffMultiplier: 1.3,      // Reduced from 1.5 for less aggressive backoff
    pingInterval: 10000,         // EMERGENCY FIX: Reduced from 15000ms (10s) for faster detection
    connectionTimeout: 5000,     // EMERGENCY FIX: Reduced from 7000ms (5s) for quicker timeouts
    enableCircuitBreaker: true
  };

  constructor(
    private connectionService: WebSocketConnectionService,
    private messageRouter: MessageRouterService,
    private reconnectionService: ReconnectionService,
    private networkService: NetworkQualityService,
    private circuitBreaker: CircuitBreakerService
  ) {
    this.setupMessageRouting();
    this.setupPingSystem();
    this.setupConnectionStateTracking();
  }

  /**
   * Setup connection state tracking for enhanced monitoring
   */
  private setupConnectionStateTracking(): void {
    // Track connection events
    this.connectionService.events$
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        if (event.type === 'status' && event.data?.code === 'connected') {
          this.lastConnectedTime = new Date();
          this.lastError = undefined;
        } else if (event.type === 'error') {
          const error = event.data?.error;
          this.lastError = error instanceof Error ? error.message :
                           (error ? String(error) : 'Unknown connection error');
        }
      });

    // Track network quality changes for connection decisions
    this.networkService.visibilityState$
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged((prev, curr) =>
          prev.isVisible === curr.isVisible &&
          prev.isFocused === curr.isFocused
        )
      )
      .subscribe(state => {
        // Optimize ping frequency based on visibility
        if (state.isVisible && state.isFocused) {
          this.optimizePingFrequency('active');
        } else if (state.isVisible) {
          this.optimizePingFrequency('visible');
        } else {
          this.optimizePingFrequency('hidden');
        }
      });
  }

  /**
   * Optimize ping frequency based on application state
   */
  private optimizePingFrequency(state: 'active' | 'visible' | 'hidden'): void {
    const baseInterval = this.config.pingInterval;
    let newInterval: number;

    switch (state) {
      case 'active':
        newInterval = baseInterval; // Normal frequency
        break;
      case 'visible':
        newInterval = baseInterval * 1.5; // Slightly reduced
        break;
      case 'hidden':
        newInterval = baseInterval * 3; // Much reduced to save resources
        break;
    }

    // Update ping interval if it changed significantly
    if (Math.abs(newInterval - baseInterval) > 5000) {
      this.config = { ...this.config, pingInterval: newInterval };
      this.restartPingSystem();
    }
  }

  // Public API - Connection Management
  connect(url: string): Observable<boolean> {
    return this.reconnectionService.connect(url);
  }

  disconnect(): void {
    this.stopPingSystem();
    this.reconnectionService.stopReconnection();
    this.connectionService.disconnect();
  }

  forceReconnect(): Observable<boolean> {
    return this.reconnectionService.forceReconnect();
  }

  // Public API - Configuration
  updateConfig(config: Partial<ConnectionConfig>): void {
    this.config = { ...this.config, ...config };
    this.reconnectionService.updateConfig(this.config);
    this.restartPingSystem();
  }

  // Public API - State Observables
  get state$(): Observable<ConnectionState> {
    return combineLatest([
      this.connectionService.status$,
      this.networkService.networkQuality$,
      this.networkService.visibilityState$
    ]).pipe(
      map(([status, quality, visibilityState]) => {
        const reconnectionInfo = this.reconnectionService.getReconnectionInfo();

        return {
          status,
          quality,
          lastConnected: this.lastConnectedTime,
          reconnectAttempts: reconnectionInfo.attempts,
          lastError: this.lastError,
          isOnline: visibilityState.isOnline && quality !== NetworkQuality.OFFLINE,
          // Enhanced reconnection tracking
          isReconnecting: reconnectionInfo.isReconnecting,
          maxReconnectAttempts: reconnectionInfo.maxAttempts,
          nextRetryDelay: reconnectionInfo.nextDelay
        };
      }),
      distinctUntilChanged((a, b) =>
        a.status === b.status &&
        a.quality === b.quality &&
        a.reconnectAttempts === b.reconnectAttempts &&
        a.isOnline === b.isOnline &&
        a.isReconnecting === b.isReconnecting
      ),
      share()
    );
  }

  get messages$(): Observable<WebSocketMessage> {
    return this.connectionService.messages$;
  }

  get events$(): Observable<ConnectionEvent> {
    return this.connectionService.events$;
  }

  // Public API - Specialized Message Streams
  get heartbeats$(): Observable<HeartbeatMessage> {
    return this.messageRouter.heartbeats$;
  }

  get pongs$(): Observable<PongMessage> {
    return this.messageRouter.pongs$;
  }

  get acks$(): Observable<AckMessage> {
    return this.messageRouter.acks$;
  }

  get userEvents$(): Observable<UserEventMessage> {
    return this.messageRouter.userEvents$;
  }

  get conversationEvents$(): Observable<ConversationEventMessage> {
    return this.messageRouter.conversationEvents$;
  }

  get messageEvents$(): Observable<MessageEventMessage> {
    return this.messageRouter.messageEvents$;
  }

  get stateEvents$(): Observable<StateEventMessage> {
    return this.messageRouter.stateEvents$;
  }

  // Public API - Message Sending
  send(data: any): boolean {
    return this.connectionService.send(data);
  }

  sendPing(connectionId?: string): boolean {
    // CIRCUIT BREAKER: Check if ping operations are allowed
    const circuitStatus = this.connectionService.getCircuitBreakerStatus();
    if (!circuitStatus.isOperational) {
      console.warn('🚫 PING BLOCKED: Circuit breaker preventing ping operations', {
        state: circuitStatus.state,
        cooldownRemaining: circuitStatus.cooldownRemaining,
        emergencyStopActive: circuitStatus.emergencyStopActive
      });
      return false;
    }

    const enhancedConnectionId = connectionId || this.generateConnectionId();
    this.lastPingTime = Date.now();

    const message: PingMessage = {
      event_type: 'ping',
      timestamp: new Date().toISOString(),
      connection_id: enhancedConnectionId
    };

    const success = this.send(message);

    if (success) {
      /*console.log('🏓 PING: Sent ping', {
        connectionId: enhancedConnectionId,
        sequence: this.pingSequenceNumber,
        timestamp: message.timestamp,
        totalActivePings: this.connectionService.getPingStats().activePings
      });*/

      this.pingSequenceNumber++;
    } else {
      console.warn('⚠️ PING: Failed to send ping', {
        connectionId: enhancedConnectionId,
        connectionStatus: this.connectionService.getStatus(),
        circuitBreakerState: circuitStatus.state
      });
      this.handlePingMiss();
    }

    return success;
  }

  sendChatMessage(userId: string, message: string): boolean {
    const chatMessage: ChatMessage = {
      event_type: 'chat',
      user_id: userId,
      message,
      timestamp: new Date().toISOString()
    };
    return this.send(chatMessage);
  }

  sendStatusMessage(status: string, userId: string): boolean {
    const statusMessage: StatusMessage = {
      event_type: 'status',
      status,
      user_id: userId,
      timestamp: new Date().toISOString()
    };
    return this.send(statusMessage);
  }

  sendFormattedMessage(eventType: string, data: any, userId?: string): boolean {
    const message = {
      event_type: eventType,
      timestamp: new Date().toISOString(),
      ...data,
      ...(userId && { user_id: userId })
    };
    return this.send(message);
  }

  // Public API - Status Checks
  isConnected(): boolean {
    return this.connectionService.isConnected();
  }

  isConnecting(): boolean {
    return this.connectionService.isConnecting();
  }

  getConnectionInfo() {
    const networkState = this.networkService.getCurrentVisibilityState();
    const reconnectionInfo = this.reconnectionService.getReconnectionInfo();
    const pingStats = this.connectionService.getPingStats();
    const circuitBreakerStatus = this.connectionService.getCircuitBreakerStatus();

    return {
      status: this.connectionService.getStatus(),
      url: this.connectionService.getCurrentUrl(),
      networkQuality: this.networkService.getCurrentQuality(),
      circuitState: this.circuitBreaker.getState(),
      // Enhanced circuit breaker information
      circuitBreaker: {
        state: circuitBreakerStatus.state,
        isOperational: circuitBreakerStatus.isOperational,
        cooldownRemaining: circuitBreakerStatus.cooldownRemaining,
        consecutiveFailures: circuitBreakerStatus.consecutiveFailures,
        emergencyStopActive: circuitBreakerStatus.emergencyStopActive
      },
      reconnection: reconnectionInfo,
      lastConnected: this.lastConnectedTime,
      lastError: this.lastError,
      isApplicationActive: this.networkService.isApplicationActive(),
      isApplicationVisible: this.networkService.isApplicationVisible(),
      ping: {
        stats: pingStats,
        missedResponses: this.missedPingResponses,
        lastPingTime: this.lastPingTime,
        sequenceNumber: this.pingSequenceNumber,
        isHealthMonitoring: !!this.pingHealthTimer
      },
      connectionHealth: {
        isHealthy: this.isConnectionHealthy(),
        qualityScore: this.getConnectionQualityScore(),
        networkStability: this.getNetworkStabilityScore()
      },
      diagnostics: {
        timeSinceLastConnection: this.lastConnectedTime ?
          Date.now() - this.lastConnectedTime.getTime() : null,
        isLikelyHibernation: this.isLikelyHibernationScenario(),
        isLikelyWiFiChange: this.isLikelyWiFiChangeScenario(),
        recommendedAction: this.getRecommendedAction()
      }
    };
  }

  /**
   * Check if connection is healthy based on multiple factors
   */
  private isConnectionHealthy(): boolean {
    const status = this.connectionService.getStatus();
    const quality = this.networkService.getCurrentQuality();
    const circuitState = this.circuitBreaker.getState();

    return status === ConnectionStatus.CONNECTED &&
           (quality === NetworkQuality.EXCELLENT || quality === NetworkQuality.GOOD) &&
           !circuitState.isOpen;
  }

  /**
   * Get connection quality score (0-100)
   */
  private getConnectionQualityScore(): number {
    const quality = this.networkService.getCurrentQuality();
    const circuitState = this.circuitBreaker.getState();
    const reconnectionInfo = this.reconnectionService.getReconnectionInfo();

    let score = 100;

    // Deduct for poor network quality
    switch (quality) {
      case NetworkQuality.POOR:
        score -= 40;
        break;
      case NetworkQuality.OFFLINE:
        score = 0;
        break;
    }

    // Deduct for circuit breaker issues
    if (circuitState.isOpen) {
      score -= 30;
    }

    // Deduct for recent reconnection attempts
    score -= Math.min(reconnectionInfo.attempts * 5, 25);

    return Math.max(0, score);
  }

  /**
   * Get network stability score based on recent patterns
   */
  private getNetworkStabilityScore(): number {
    const networkState = this.networkService.getCurrentVisibilityState();
    const timeSinceLastCheck = Date.now() - networkState.lastCheck.getTime();

    let score = 100;

    // Recent check means stable monitoring
    if (timeSinceLastCheck < 30000) {
      score = 100;
    } else if (timeSinceLastCheck < 60000) {
      score = 70;
    } else {
      score = 40; // Long gap suggests instability
    }

    return score;
  }

  /**
   * Check if current scenario looks like hibernation recovery
   */
  private isLikelyHibernationScenario(): boolean {
    const timeSinceLastConnection = this.lastConnectedTime ?
      Date.now() - this.lastConnectedTime.getTime() : 0;

    return timeSinceLastConnection > 60000; // 1 minute suggests hibernation
  }

  /**
   * Check if current scenario looks like WiFi change
   */
  private isLikelyWiFiChangeScenario(): boolean {
    const reconnectionInfo = this.reconnectionService.getReconnectionInfo();
    const circuitState = this.circuitBreaker.getState();

    // Rapid reconnection attempts with circuit breaker activity
    return reconnectionInfo.attempts > 1 &&
           reconnectionInfo.attempts < 5 &&
           circuitState.failureCount > 0;
  }

  /**
   * Get recommended action based on current state
   */
  private getRecommendedAction(): string {
    if (this.isLikelyHibernationScenario()) {
      return 'hibernation_recovery';
    }

    if (this.isLikelyWiFiChangeScenario()) {
      return 'wifi_change_recovery';
    }

    const quality = this.networkService.getCurrentQuality();
    if (quality === NetworkQuality.OFFLINE) {
      return 'wait_for_network';
    }

    if (quality === NetworkQuality.POOR) {
      return 'wait_for_better_network';
    }

    const circuitState = this.circuitBreaker.getState();
    if (circuitState.isOpen) {
      return 'wait_for_circuit_recovery';
    }

    return 'normal_operation';
  }

  // Private methods
  private setupMessageRouting(): void {
    this.connectionService.messages$
      .pipe(takeUntil(this.destroy$))
      .subscribe(message => {
        this.messageRouter.routeMessage(message);
      });
  }

  private setupPingSystem(): void {
    this.connectionService.status$
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged()
      )
      .subscribe(status => {
        if (status === ConnectionStatus.CONNECTED) {
          // console.log('🏓 PING SYSTEM: Connection established, starting ping system');
          // IMMEDIATE START: Start ping system without delay for new connections
          this.startPingSystemImmediate();
        } else {
          // console.log('🏓 PING SYSTEM: Connection lost, stopping ping system');
          this.stopPingSystem();
        }
      });

    // Enhanced: Listen for connection verification events and ping timeouts
    this.connectionService.events$
      .pipe(
        takeUntil(this.destroy$),
        filter((event: any) => event.type === 'status' ||
               event.type === 'message' ||
               event.type === 'error')
      )
      .subscribe((event: any) => {
        if (event.data?.verification_success) {
          console.log('✅ PING SYSTEM: Connection verification successful');
          this.missedPingResponses = 0; // Reset miss counter
        } else if (event.data?.verification_timeout) {
          console.warn('⚠️ PING SYSTEM: Connection verification timeout');
          this.handlePingMiss();
        } else if (event.type === 'message' && event.data?.ping_timeout) {
          console.warn('⚠️ PING SYSTEM: Ping timeout detected');
          this.handlePingMiss();
        } else if (event.type === 'message' && event.data?.ping_response) {
          console.log('✅ PING SYSTEM: Ping response received');
          this.handlePingSuccess();
        } else if (event.type === 'message' && event.data?.ping_health_warning) {
          console.warn('⚠️ PING SYSTEM: Health warning - low response rate');
          this.handlePingHealthWarning(event.data);
        } else if (event.type === 'message' && event.data?.ping_max_failures_reached) {
          console.error('❌ PING SYSTEM: Maximum consecutive ping failures reached');
          this.handleMaxPingFailures(event.data);
        } else if (event.type === 'message' && event.data?.connection_health_critical) {
          console.error('🚨 PING SYSTEM: Connection health critical - triggering reconnection');
          this.handleCriticalConnectionHealth(event.data);
        } else if (event.type === 'message' && event.data?.ping_emergency_low_rate) {
          console.error('🚨 EMERGENCY: Critical response rate detected');
          this.handleEmergencyLowResponseRate(event.data);
        } else if (event.type === 'message' && event.data?.ping_emergency_override) {
          console.error('🚨 EMERGENCY OVERRIDE: Force action required');
          this.handleEmergencyOverride(event.data);
        }
      });
  }

  private startPingSystem(): void {
    // CIRCUIT BREAKER: Check if ping system can be started
    const circuitStatus = this.connectionService.getCircuitBreakerStatus();
    if (!circuitStatus.isOperational) {
      console.warn('🚫 PING SYSTEM: Cannot start - circuit breaker is blocking operations', {
        state: circuitStatus.state,
        cooldownRemaining: circuitStatus.cooldownRemaining,
        emergencyStopActive: circuitStatus.emergencyStopActive
      });

      // Schedule retry after cooldown period
      if (circuitStatus.cooldownRemaining > 0) {
        setTimeout(() => {
          console.log('🔄 PING SYSTEM: Retrying start after cooldown period');
          this.startPingSystem();
        }, circuitStatus.cooldownRemaining + 1000); // Add 1 second buffer
      }
      return;
    }

    this.stopPingSystem();

    if (this.config.pingInterval > 0) {
      this.pingTimer = window.setInterval(() => {
        if (this.isConnected()) {
          // Double-check circuit breaker before each ping
          const currentCircuitStatus = this.connectionService.getCircuitBreakerStatus();
          if (currentCircuitStatus.isOperational) {
            this.sendPing();
          } else {
            console.warn('🚫 PING SYSTEM: Stopping due to circuit breaker activation');
            this.stopPingSystem();
          }
        }
      }, this.config.pingInterval);
    }
  }

  /**
   * PING SYSTEM: Start ping system immediately for new connections
   */
  private startPingSystemImmediate(): void {
    console.log('🏓 PING SYSTEM: Starting immediate ping system');

    // CIRCUIT BREAKER: Check if ping system can be started
    const circuitStatus = this.connectionService.getCircuitBreakerStatus();
    if (!circuitStatus.isOperational) {
      console.warn('🚫 PING SYSTEM: Cannot start immediate system - circuit breaker is blocking', {
        state: circuitStatus.state,
        cooldownRemaining: circuitStatus.cooldownRemaining,
        emergencyStopActive: circuitStatus.emergencyStopActive
      });

      // Schedule retry after cooldown period
      if (circuitStatus.cooldownRemaining > 0) {
        setTimeout(() => {
          console.log('🔄 PING SYSTEM: Retrying immediate start after cooldown period');
          this.startPingSystemImmediate();
        }, circuitStatus.cooldownRemaining + 1000); // Add 1 second buffer
      }
      return;
    }

    this.stopPingSystem();

    if (this.config.pingInterval > 0) {
      // Send first ping immediately (with circuit breaker check)
      if (this.isConnected()) {
        console.log('🏓 PING SYSTEM: Sending first ping immediately');
        this.sendPing();
      }

      // Then start regular interval
      this.pingTimer = window.setInterval(() => {
        if (this.isConnected()) {
          // Double-check circuit breaker before each ping
          const currentCircuitStatus = this.connectionService.getCircuitBreakerStatus();
          if (currentCircuitStatus.isOperational) {
            this.sendPing();
          } else {
            console.warn('🚫 PING SYSTEM: Stopping due to circuit breaker activation during interval');
            this.stopPingSystem();
          }
        } else {
          console.log('🏓 PING SYSTEM: Connection lost, stopping ping timer');
          this.stopPingSystem();
        }
      }, this.config.pingInterval);

      console.log('🏓 PING SYSTEM: Regular ping interval started:', {
        interval: this.config.pingInterval,
        intervalId: this.pingTimer
      });
    }
  }

  private stopPingSystem(): void {
    if (this.pingTimer) {
      window.clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private restartPingSystem(): void {
    if (this.isConnected()) {
      this.startPingSystem();
    }
  }

  private generateConnectionId(): string {
    return `conn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Handle ping miss/timeout events
   */
  private handlePingMiss(): void {
    this.missedPingResponses++;

    const pingStats = this.connectionService.getPingStats();

    console.warn('⚠️ PING SYSTEM: Ping miss detected', {
      missed: this.missedPingResponses,
      sequence: this.pingSequenceNumber,
      lastPingTime: this.lastPingTime,
      connectionStatus: this.connectionService.getStatus(),
      consecutiveFailures: pingStats.consecutiveFailures,
      responseRate: `${pingStats.responseRate.toFixed(1)}%`
    });

    // Note: The actual consecutive failure handling is now done in WebSocketConnectionService
    // This method now primarily tracks local missed responses for additional monitoring

    // Emergency check: If we have excessive local misses AND connection service reports failures
    if (this.missedPingResponses >= 6 && pingStats.consecutiveFailures >= 3) {
      console.error('❌ PING SYSTEM: Excessive ping misses detected locally and in connection service');

      // Get comprehensive ping stats before taking emergency action
      console.log('📊 PING STATS:', pingStats);

      const timeSinceLastResponse = pingStats.timeSinceLastResponse;
      if (timeSinceLastResponse > 45000) { // 45 seconds without response
        console.error('🚨 EMERGENCY: No ping responses for 45+ seconds, forcing immediate reconnection');
        this.enableEmergencyRecoveryMode();
        return;
      }
    }

    // Traditional response rate check (backup mechanism)
    if (pingStats.responseRate < 30 && this.missedPingResponses >= 4) {
      console.error('❌ PING SYSTEM: Critical response rate with multiple misses, triggering circuit breaker');
      this.circuitBreaker.recordFailure();
    }
  }

  /**
   * Emergency recovery mode for severe connection issues
   */
  private enableEmergencyRecoveryMode(): void {
    console.error('🚨 EMERGENCY RECOVERY: Activating emergency recovery mode');

    // Reset ping counters and force immediate reconnection
    this.missedPingResponses = 0;

    // Trigger immediate reconnection without waiting
    this.forceReconnect().subscribe({
      next: (success) => {
        if (success) {
          console.log('✅ EMERGENCY: Recovery successful');
        } else {
          console.error('❌ EMERGENCY: Recovery failed, will retry');
        }
      },
      error: (error) => {
        console.error('❌ EMERGENCY: Recovery error:', error);
      }
    });
  }

  /**
   * Handle successful ping response
   */
  private handlePingSuccess(): void {
    this.missedPingResponses = 0; // Reset miss counter
    this.circuitBreaker.recordSuccess(); // Record successful communication

    console.log('✅ PING SYSTEM: Ping success, connection healthy');
  }

  /**
   * Handle maximum ping failures - trigger immediate reconnection
   */
  private handleMaxPingFailures(data: any): void {
    const consecutiveFailures = data.consecutiveFailures || 0;
    const responseRate = data.pingResponseRate || 0;

    console.error('❌ PING SYSTEM: Maximum consecutive ping failures reached', {
      consecutiveFailures,
      responseRate: `${responseRate.toFixed(1)}%`,
      action: 'Triggering immediate reconnection'
    });

    // Reset ping counters
    this.missedPingResponses = 0;

    // Record failure in circuit breaker
    this.circuitBreaker.recordFailure();

    // Force immediate reconnection due to ping failures
    setTimeout(() => {
      console.log('🔄 PING SYSTEM: Executing forced reconnection due to ping failures');
      this.forceReconnect().subscribe({
        next: (success) => {
          if (success) {
            console.log('✅ PING SYSTEM: Reconnection successful after ping failures');
          } else {
            console.error('❌ PING SYSTEM: Reconnection failed after ping failures');
          }
        },
        error: (error) => {
          console.error('❌ PING SYSTEM: Reconnection error after ping failures:', error);
        }
      });
    }, 1000); // 1 second delay to allow cleanup
  }

  /**
   * Handle critical connection health - evaluate and potentially reconnect
   */
  private handleCriticalConnectionHealth(data: any): void {
    const reason = data.reason || 'unknown';
    const failureCount = data.failureCount || 0;
    const responseRate = data.responseRate || 0;

    console.error('🚨 PING SYSTEM: Critical connection health detected', {
      reason,
      failureCount,
      responseRate: `${responseRate.toFixed(1)}%`,
      currentStatus: this.connectionService.getStatus(),
      isConnected: this.isConnected()
    });

    // Check if connection appears active but is actually unhealthy
    if (this.isConnected()) {
      console.warn('⚠️ PING SYSTEM: Connection appears active but health is critical');

      // Get comprehensive ping statistics
      const pingStats = this.connectionService.getPingStats();

      console.log('📊 PING SYSTEM: Current ping statistics', {
        activePings: pingStats.activePings,
        responseRate: `${pingStats.responseRate.toFixed(1)}%`,
        consecutiveFailures: pingStats.consecutiveFailures,
        maxConsecutiveFailures: pingStats.maxConsecutiveFailures,
        timeSinceLastResponse: `${pingStats.timeSinceLastResponse}ms`
      });

      // If no response in the last 30 seconds and multiple failures, force reconnection
      if (pingStats.timeSinceLastResponse > 30000 && pingStats.consecutiveFailures >= 4) {
        console.error('❌ PING SYSTEM: No response in 30+ seconds with 4+ failures, forcing reconnection');
        this.enableEmergencyRecoveryMode();
      }
      // If response rate is critically low, schedule reconnection
      else if (pingStats.responseRate < 25) {
        console.error('❌ PING SYSTEM: Response rate critically low, scheduling reconnection');
        setTimeout(() => {
          this.forceReconnect().subscribe();
        }, 2000);
      }
    }
  }

  /**
   * Handle emergency low response rate - immediate action required
   */
  private handleEmergencyLowResponseRate(data: any): void {
    const responseRate = data.responseRate || 0;
    const consecutiveFailures = data.consecutiveFailures || 0;
    const timeSinceLastResponse = data.timeSinceLastResponse || 0;

    console.error('🚨 EMERGENCY: Critical response rate detected', {
      responseRate: `${responseRate.toFixed(1)}%`,
      consecutiveFailures,
      timeSinceLastResponse: `${timeSinceLastResponse}ms`,
      currentStatus: this.connectionService.getStatus(),
      action: 'Emergency reconnection triggered'
    });

    // Reset ping counters
    this.missedPingResponses = 0;

    // Record critical failure
    this.circuitBreaker.recordFailure();

    // EMERGENCY: Force immediate reconnection without waiting
    console.log('🚨 EMERGENCY: Executing immediate reconnection due to critical response rate');

    setTimeout(() => {
      this.forceReconnect().subscribe({
        next: (success) => {
          if (success) {
            console.log('✅ EMERGENCY: Critical response rate recovery successful');
          } else {
            console.error('❌ EMERGENCY: Critical response rate recovery failed');
          }
        },
        error: (error) => {
          console.error('❌ EMERGENCY: Critical response rate recovery error:', error);
        }
      });
    }, 500); // 500ms delay for emergency situations
  }

  /**
   * Handle emergency override - bypass event system for immediate action
   */
  private handleEmergencyOverride(data: any): void {
    const responseRate = data.responseRate || 0;
    const consecutiveFailures = data.consecutiveFailures || 0;
    const severity = data.severity || 'critical';

    console.error('🚨 EMERGENCY OVERRIDE: Bypassing normal event system', {
      responseRate: `${responseRate.toFixed(1)}%`,
      consecutiveFailures,
      severity,
      action: 'Direct reconnection without delays'
    });

    // OVERRIDE: Stop ping system immediately
    this.stopPingSystem();

    // Reset all counters
    this.missedPingResponses = 0;
    this.pingSequenceNumber = 0;

    // Record critical override event
    this.circuitBreaker.recordFailure();

    // OVERRIDE: Direct reconnection with no delays
    console.log('🚨 OVERRIDE: Executing direct reconnection bypass');

    setTimeout(() => {
      this.forceReconnect().subscribe({
        next: (success) => {
          if (success) {
            console.log('✅ OVERRIDE: Emergency bypass recovery successful');
          } else {
            console.error('❌ OVERRIDE: Emergency bypass recovery failed');
          }
        },
        error: (error) => {
          console.error('❌ OVERRIDE: Emergency bypass recovery error:', error);
        }
      });
    }, 100); // 100ms delay for emergency override
  }

  private handlePingHealthWarning(data: any): void {
    const responseRate = data.responseRate || 0;
    const activePings = data.activePings || 0;

    console.warn('⚠️ PING SYSTEM: Health warning', {
      responseRate: `${responseRate.toFixed(1)}%`,
      activePings,
      recommendation: 'Monitor connection stability',
      missedResponses: this.missedPingResponses
    });

    // Only start intensive monitoring if response rate is very low
    if (responseRate < 20) {
      this.startPingHealthMonitoring();
    } else {
      console.log('ℹ️ PING SYSTEM: Health warning but response rate acceptable, continuing normal monitoring');
    }
  }

  /**
   * Start enhanced ping health monitoring
   */
  private startPingHealthMonitoring(): void {
    if (this.pingHealthTimer) {
      return; // Already monitoring
    }

    console.log('🏥 PING SYSTEM: Starting intensive health monitoring');

    this.pingHealthTimer = window.setInterval(() => {
      const pingStats = this.connectionService.getPingStats();
      const timeSinceLastResponse = pingStats.timeSinceLastResponse;

      console.log('🏥 PING HEALTH:', {
        responseRate: `${pingStats.responseRate.toFixed(1)}%`,
        activePings: pingStats.activePings,
        timeSinceLastResponse: `${timeSinceLastResponse}ms`,
        missedResponses: this.missedPingResponses,
        connectionStatus: this.connectionService.getStatus()
      });

      // If no response in 3 minutes, force action
      if (timeSinceLastResponse > 180000) {
        console.error('❌ PING HEALTH: No response in 3 minutes, forcing reconnection');
        this.stopPingHealthMonitoring();
        this.forceReconnect().subscribe();
      }
      // If response rate improved significantly, stop monitoring
      else if (pingStats.responseRate > 60 && this.missedPingResponses < 3) {
        console.log('✅ PING HEALTH: Health significantly improved, stopping intensive monitoring');
        this.stopPingHealthMonitoring();
      }
    }, 45000); // Check every 45 seconds for intensive monitoring
  }

  /**
   * Stop ping health monitoring
   */
  private stopPingHealthMonitoring(): void {
    if (this.pingHealthTimer) {
      window.clearInterval(this.pingHealthTimer);
      this.pingHealthTimer = null;
      console.log('🏥 PING SYSTEM: Stopped intensive health monitoring');
    }
  }

  ngOnDestroy(): void {
    this.stopPingSystem();
    this.stopPingHealthMonitoring();
    this.reconnectionService.stopReconnection();
    this.connectionService.disconnect();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
